import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Zap, Check, Copy, MessageCircle, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const PERKS = [
  "First month completely free — no card needed",
  "Unlimited product listings during your founder month",
  "AI matches your products to buyers actively searching",
  "Direct payouts to your bank — you keep your sales",
];

export default function FoundersWelcome() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [promo, setPromo] = useState(null);
  const [refCode, setRefCode] = useState(null);

  useEffect(() => {
    api.get("/billing/founders-promo").then(({ data }) => setPromo(data)).catch(() => {});
    api.get("/referrals/me").then(({ data }) => setRefCode(data.code)).catch(() => {});
  }, []);

  const num = user?.founders_number;
  const cap = promo?.cap ?? 20;
  const shareLink = refCode
    ? `${window.location.origin}/register?role=seller&ref=${refCode}`
    : `${window.location.origin}/register?role=seller`;
  const shareMsg = `I just claimed founding seller spot${num ? ` #${num}` : ""} on VISMARKS — first month free, unlimited listings and AI-matched buyers. Only ${promo?.remaining ?? "a few"} of ${cap} founder spots left. Grab yours: ${shareLink}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      toast.success("Your invite link is copied — share it anywhere");
    } catch {
      toast.error("Could not copy — long-press the link to copy manually");
    }
  };

  return (
    <div data-testid="founders-welcome-page" className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-6 lg:p-12 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(255,59,48,0.18), transparent 60%)" }} />
      <div className="w-full max-w-2xl relative">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#FF3B30]/50 bg-[#FF3B30]/10 text-[#FF3B30] text-xs font-bold uppercase tracking-widest">
            <Zap size={14} /> Founding Seller
          </div>
          <h1 className="mt-6 font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl">
            You're in.
          </h1>
          {num ? (
            <p data-testid="founder-number" className="mt-4 text-lg text-neutral-300">
              You are founding seller <span className="font-black text-white">#{num}</span> of {cap} — a spot nobody else will ever hold.
            </p>
          ) : (
            <p data-testid="founder-number" className="mt-4 text-lg text-neutral-300">
              You claimed one of the {cap} founding seller spots.
            </p>
          )}
        </div>

        <div className="mt-10 rounded-2xl border border-neutral-800 bg-neutral-950/80 p-6 sm:p-8">
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-bold">Your founder perks</div>
          <ul className="mt-4 space-y-3">
            {PERKS.map((p) => (
              <li key={p} className="flex items-start gap-3 text-sm text-neutral-200">
                <span className="mt-0.5 rounded-full bg-[#FF3B30]/15 text-[#FF3B30] p-1"><Check size={13} /></span>
                {p}
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-6 rounded-2xl border border-neutral-800 bg-neutral-950/80 p-6 sm:p-8">
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-bold">Tell your people</div>
          <p className="mt-2 text-sm text-neutral-400">
            Every seller you invite who pays earns you a free month. Share your founder story:
          </p>
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <a
              data-testid="founders-share-whatsapp"
              href={`https://wa.me/?text=${encodeURIComponent(shareMsg)}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-black font-bold text-sm px-6 py-3 hover:opacity-90 transition-opacity"
            >
              <MessageCircle size={16} /> Share on WhatsApp
            </a>
            <button
              data-testid="founders-copy-link"
              onClick={copyLink}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-neutral-700 text-sm font-bold px-6 py-3 hover:bg-neutral-900 transition-colors"
            >
              <Copy size={15} /> Copy invite link
            </button>
          </div>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <button
            data-testid="founders-list-first-product"
            onClick={() => nav("/seller/new")}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FF3B30] text-white font-bold text-sm px-8 py-3.5 hover:bg-[#e0342a] transition-colors"
          >
            List your first product <ArrowRight size={16} />
          </button>
          <button
            data-testid="founders-go-dashboard"
            onClick={() => nav("/seller")}
            className="inline-flex items-center justify-center rounded-full border border-neutral-700 text-sm font-bold px-8 py-3.5 hover:bg-neutral-900 transition-colors"
          >
            Go to dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
